import {
  collection, doc, query, where, or, onSnapshot, getDocs,
  runTransaction, deleteDoc, serverTimestamp
} from "https://www.gstatic.com/firebasejs/12.18.0/firebase-firestore.js";
import { onAuthStateChanged } from "https://www.gstatic.com/firebasejs/12.18.0/firebase-auth.js";
import { favoriteProviderId, favoriteProviderAvailable, activeFavoriteMenu, FAVORITES_TEXT } from "../modules/provider-favorites.js";

let user = null;
let generation = 0;
let favorites = new Map();
let providerStates = new Map();
let providerListeners = new Map();
let unsubscribeFavorites = null;
let unsubscribeMenu = null;
let selectedProviderId = "";
let menuItems = [];
let menuStatus = "";
let loading = true;
let loadError = false;
let fromCache = false;
const pending = new Set();

function text(key) {
  const language = document.getElementById("languageSelector")?.value || "en";
  return (FAVORITES_TEXT[language] || FAVORITES_TEXT.en)[key];
}
function escape(value) {
  return String(value || "").replace(/[&<>"']/g, (char) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"
  })[char]);
}
function notify(key, error) {
  if (error) console.error("[Provider Favorites]", error);
  window.showNotification(text(key), error ? "error" : "success");
}
function favoriteRef(uid, providerId) {
  return doc(window.firebaseDbRaw, "clients", uid, "favoriteProviders", providerId);
}
function clearSession() {
  generation += 1;
  unsubscribeFavorites?.();
  unsubscribeMenu?.();
  for (const stop of providerListeners.values()) stop();
  unsubscribeFavorites = null;
  unsubscribeMenu = null;
  providerListeners.clear();
  providerStates.clear();
  favorites.clear();
  pending.clear();
  user = null;
  selectedProviderId = "";
  menuItems = [];
  menuStatus = "";
  loading = true;
  loadError = false;
  document.getElementById("favoriteProviderMenu").replaceChildren();
  document.getElementById("favoriteProvidersList").replaceChildren();
}

window.favoriteProviderButton = function favoriteProviderButton(record) {
  const id = favoriteProviderId(record);
  if (!id) return "";
  return `<button type="button" class="favorite-provider-btn" data-favorite-provider="${escape(id)}"
    data-provider-name="${escape(record.providerName || "")}" aria-pressed="${favorites.has(id)}"
    ${!user || loading || loadError || pending.has(id) ? "disabled" : ""}>${escape(text(favorites.has(id) ? "remove" : "save"))}</button>`;
};

function updateButtons() {
  for (const button of document.querySelectorAll("[data-favorite-provider]")) {
    const id = button.dataset.favoriteProvider;
    button.textContent = text(favorites.has(id) ? "remove" : "save");
    button.setAttribute("aria-pressed", String(favorites.has(id)));
    button.disabled = !user || loading || loadError || pending.has(id);
  }
}
function renderMenu() {
  const container = document.getElementById("favoriteProviderMenu");
  if (!selectedProviderId) {
    container.replaceChildren();
    return;
  }
  const state = providerStates.get(selectedProviderId);
  document.getElementById("favoriteProviderMenuTitle").textContent =
    state?.data?.name || favorites.get(selectedProviderId)?.providerName || selectedProviderId;
  if (!state?.data || !favoriteProviderAvailable(state.data)) {
    container.textContent = text(state?.error ? "providerError" : "unavailable");
    return;
  }
  if (menuStatus === "loading") {
    container.textContent = text("menuLoading");
    return;
  }
  if (menuStatus === "error") {
    container.textContent = text("menuError");
    const retry = document.createElement("button");
    retry.type = "button";
    retry.textContent = text("retry");
    retry.addEventListener("click", () => openMenu(selectedProviderId));
    container.appendChild(retry);
    return;
  }
  const active = activeFavoriteMenu(menuItems, selectedProviderId).map((item) => ({
    ...item, providerName: state.data.name || item.providerName
  }));
  if (!active.length) {
    container.textContent = text("noMenu");
    return;
  }
  window.renderClientSearchResults({ items: active, containerId: "favoriteProviderMenu" });
}
function render() {
  document.querySelector('[data-dashboard-tab="favorites"]').textContent = text("tab");
  document.getElementById("favoriteProvidersTitle").textContent = text("tab");
  document.getElementById("favoriteProvidersIntro").textContent = text("intro");
  document.getElementById("favoriteProvidersBack").textContent = text("back");
  const list = document.getElementById("favoriteProvidersList");
  const status = document.getElementById("favoriteProvidersStatus");
  document.getElementById("favoriteProvidersRetry").textContent = text("retry");
  document.getElementById("favoriteProvidersRetry").classList.toggle("hidden", !loadError);
  status.textContent = text(loadError ? "error" : loading ? "loading" : fromCache ? "cached" : "live");
  list.setAttribute("aria-busy", String(loading));
  if (!loading && !loadError && !favorites.size) {
    list.textContent = text("empty");
  } else {
    list.innerHTML = Array.from(favorites.entries()).map(([id, favorite]) => {
      const state = providerStates.get(id);
      const available = favoriteProviderAvailable(state?.data);
      return `<article class="favorite-provider-card">
        <h4>${escape(state?.data?.name || favorite.providerName || id)}</h4>
        <p>${escape(state?.error ? text("providerError") : !state ? text("loading") :
          available ? state.data.address || "" : text("unavailable"))}</p>
        <div class="card-actions">
          <button type="button" class="contact-btn" data-favorite-menu="${escape(id)}" ${available && user && !loadError ? "" : "disabled"}>${escape(text("view"))}</button>
          ${window.favoriteProviderButton({ providerId: id, providerName: favorite.providerName })}
        </div>
      </article>`;
    }).join("");
  }
  document.getElementById("favoriteProvidersMenuSection").classList.toggle("hidden", !selectedProviderId);
  updateButtons();
}

window.initClientFavorites = function initClientFavorites(authUser) {
  if (!authUser || !authUser.emailVerified) return;
  if (user?.uid === authUser.uid && unsubscribeFavorites && !loadError) return;
  clearSession();
  user = authUser;
  const currentGeneration = generation;
  render();
  unsubscribeFavorites = onSnapshot(collection(window.firebaseDbRaw, "clients", user.uid, "favoriteProviders"),
    { includeMetadataChanges: true }, (snapshot) => {
      if (currentGeneration !== generation) return;
      // Do not show a successful save before Firestore acknowledges the write.
      if (snapshot.metadata.hasPendingWrites) return;
      favorites = new Map(snapshot.docs.map((item) => [item.id, item.data()]));
      loading = false;
      loadError = false;
      fromCache = snapshot.metadata.fromCache;
      for (const [id, stop] of providerListeners) {
        if (!favorites.has(id)) {
          stop();
          providerListeners.delete(id);
          providerStates.delete(id);
        }
      }
      if (selectedProviderId && !favorites.has(selectedProviderId)) closeMenu();
      for (const id of favorites.keys()) {
        if (providerListeners.has(id)) continue;
        providerListeners.set(id, onSnapshot(doc(window.firebaseDbRaw, "providers", id), (provider) => {
          if (currentGeneration !== generation) return;
          providerStates.set(id, { data: provider.exists() ? provider.data() : null });
          render();
          renderMenu();
        }, (error) => {
          if (currentGeneration !== generation) return;
          console.error("[Provider Favorites] Provider listener failed:", error);
          providerStates.set(id, { error });
          render();
          renderMenu();
        }));
      }
      render();
    }, (error) => {
      if (currentGeneration !== generation) return;
      console.error("[Provider Favorites] Favorites listener failed:", error);
      loading = false;
      loadError = true;
      render();
    });
};

async function toggleFavorite(id, providerName) {
  if (!user || window.firebaseAuthInstance.currentUser?.uid !== user.uid || pending.has(id)) return;
  if (navigator.onLine === false) {
    notify("saveError", new Error("Favorites require an internet connection."));
    return;
  }
  const uid = user.uid;
  const currentGeneration = generation;
  const removing = favorites.has(id);
  pending.add(id);
  updateButtons();
  try {
    if (removing) await deleteDoc(favoriteRef(uid, id));
    else {
      await runTransaction(window.firebaseDbRaw, async (transaction) => {
        const ref = favoriteRef(uid, id);
        const existing = await transaction.get(ref);
        if (existing.exists()) return;
        const provider = await transaction.get(doc(window.firebaseDbRaw, "providers", id));
        if (!provider.exists()) throw new Error("Provider no longer exists.");
        const name = String(provider.data().name || providerName || id).slice(0, 160);
        transaction.set(ref, { providerId: id, providerName: name, createdAt: serverTimestamp() });
      });
    }
    if (currentGeneration !== generation) return;
    notify(removing ? "removed" : "saved");
  } catch (error) {
    if (currentGeneration === generation) notify("saveError", error);
  } finally {
    if (currentGeneration === generation) {
      pending.delete(id);
      updateButtons();
    }
  }
}

function closeMenu() {
  unsubscribeMenu?.();
  unsubscribeMenu = null;
  selectedProviderId = "";
  menuItems = [];
  menuStatus = "";
  render();
  renderMenu();
}
function openMenu(id) {
  if (!user || !favorites.has(id)) return;
  unsubscribeMenu?.();
  selectedProviderId = id;
  menuItems = [];
  menuStatus = "loading";
  render();
  document.getElementById("favoriteProviderMenu").textContent = text("menuLoading");
  const currentGeneration = generation;
  // This is a dedicated full provider query, independent of country filters or pagination.
  unsubscribeMenu = onSnapshot(query(collection(window.firebaseDbRaw, "menuItems"),
    or(where("providerId", "==", id), where("providerUid", "==", id))), (snapshot) => {
      if (currentGeneration !== generation || selectedProviderId !== id) return;
      menuStatus = "ready";
      menuItems = snapshot.docs.map((item) => ({
        ...item.data(), id: item.id,
        providerName: providerStates.get(id)?.data?.name || item.data().providerName || "",
        foodName: item.data().foodName || "",
        foodDesc: item.data().foodDesc || ""
      }));
      window.mergeFavoriteMenuItems(menuItems);
      renderMenu();
    }, (error) => {
      if (currentGeneration !== generation || selectedProviderId !== id) return;
      console.error("[Provider Favorites] Menu listener failed:", error);
      menuStatus = "error";
      renderMenu();
    });
}

window.exportClientFavorites = async function exportClientFavorites() {
  if (!user) throw new Error("Client favorites are unavailable without a client session.");
  const snapshot = await getDocs(collection(window.firebaseDbRaw, "clients", user.uid, "favoriteProviders"));
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
};
document.addEventListener("click", (event) => {
  const save = event.target.closest("[data-favorite-provider]");
  if (save && !save.disabled) toggleFavorite(save.dataset.favoriteProvider, save.dataset.providerName);
  const menu = event.target.closest("[data-favorite-menu]");
  if (menu && !menu.disabled) openMenu(menu.dataset.favoriteMenu);
});
// Order and gift actions share the application's menu lookup even if another
// paginated snapshot replaced the global menu array after this menu loaded.
document.getElementById("favoriteProviderMenu").addEventListener("click", () => {
  window.mergeFavoriteMenuItems(menuItems);
}, true);
document.getElementById("favoriteProvidersBack").addEventListener("click", closeMenu);
document.getElementById("favoriteProvidersRetry").addEventListener("click", () => {
  const authUser = window.firebaseAuthInstance.currentUser;
  if (authUser) window.initClientFavorites(authUser);
});
window.addEventListener("languageChanged", () => { render(); renderMenu(); });
window.addEventListener("online", () => {
  if (user) window.initClientFavorites(window.firebaseAuthInstance.currentUser);
});
window.ensureFirebaseReady().then(() => {
  onAuthStateChanged(window.firebaseAuthInstance, (authUser) => {
    if (user && authUser?.uid !== user.uid) { clearSession(); render(); updateButtons(); }
  });
}).catch((error) => {
  console.error("[Provider Favorites] Authentication initialization failed:", error);
  loading = false;
  loadError = true;
  render();
});
